/** @typedef {"ringing"|"accepted"|"joined"|"left"|"declined"|"missed"|"cancelled"} ParticipantState */
/** @typedef {{id: string, thread_id: string, kind: "audio"|"video", status: "ringing"|"active"|"ended", participants: Array<{user: {id: number, display_name: string}, state: ParticipantState}>}} Call */
/** @typedef {{url: string, room: string, identity: string, token: string, expires_at: number}} Credentials */

/** Session phases. Only these values are valid for `state.calls.session.phase`. */
export const PHASES = Object.freeze({
  idle: "idle",
  preview: "preview",
  acceptedPreview: "accepted-preview",
  resumePreview: "resume-preview",
  connecting: "connecting",
  waiting: "waiting",
  connected: "connected",
  reconnecting: "reconnecting",
});

/**
 * Every phase change the controller makes, from → allowed next phases.
 * Clearing the session (hang-up, logout, call ended) returns to idle from any phase and is not listed.
 * Repeating the current phase (for example a new waiting message) is always allowed.
 */
export const PHASE_TRANSITIONS = Object.freeze({
  // Start a call, accept an invitation, or open Resume/Rejoin.
  [PHASES.idle]: [PHASES.preview, PHASES.acceptedPreview, PHASES.resumePreview],
  // Connect; or an uncertain Start/a server "left" turns it into Resume.
  [PHASES.preview]: [PHASES.connecting, PHASES.resumePreview],
  [PHASES.acceptedPreview]: [PHASES.connecting, PHASES.resumePreview],
  [PHASES.resumePreview]: [PHASES.connecting],
  // In the room alone or with others; a failed connect falls back to Resume.
  [PHASES.connecting]: [
    PHASES.waiting,
    PHASES.connected,
    PHASES.reconnecting,
    PHASES.resumePreview,
  ],
  [PHASES.waiting]: [PHASES.connected, PHASES.reconnecting],
  [PHASES.connected]: [PHASES.waiting, PHASES.reconnecting],
  [PHASES.reconnecting]: [PHASES.connected, PHASES.waiting, PHASES.resumePreview],
});

export const canTransition = (from, to) =>
  from === to || Boolean(PHASE_TRANSITIONS[from]?.includes(to));

const PREPARING_PHASES = [PHASES.preview, PHASES.acceptedPreview, PHASES.resumePreview];
/** Local preview open, not yet connected to media. */
export const isPreparing = (phase) => PREPARING_PHASES.includes(phase);
/** In the media room (or joining it) but nobody else is connected yet. */
export const isWaiting = (phase) => phase === PHASES.waiting || phase === PHASES.connecting;
export const isIdle = (phase) => phase === PHASES.idle;

export const sameId = (a, b) =>
  a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);
export const participantFor = (call, userId) =>
  call?.participants?.find((participant) => sameId(participant.user?.id, userId));
export const isLive = (call) =>
  !call?.unavailable && (call?.status === "ringing" || call?.status === "active");
export const isInRoom = (state) => state === "accepted" || state === "joined";
// Unknown counts are treated as occupied; only an explicit 0 means the room is empty.
export const hasPeople = (call) => Number(call?.in_room_count ?? 1) > 0;
export const threadIdFor = (thread) =>
  thread?.threadId ||
  thread?.thread_id ||
  (thread?.thread_type || thread?.has_thread ? thread.id : null);
export const threadName = (thread) =>
  thread?.name ||
  thread?.display_name ||
  [thread?.first_name, thread?.last_name].filter(Boolean).join(" ") ||
  thread?.case_display ||
  "this conversation";

/** The host started the call. The call keeps going when the host leaves; only "End for all" ends it. */
export const isCallHost = (call, userId) =>
  sameId(call?.started_by?.id ?? call?.started_by_id, userId);

const displayNameFor = (call, userId) =>
  participantFor(call, userId)?.user?.display_name ||
  (isCallHost(call, userId) ? call?.started_by?.display_name : null);

/**
 * "<name> has ended the call." when someone other than `userId` ended it for everyone; else null.
 * Only an ended-on-purpose call (end_reason "completed") carries ended_by: the host or a firm admin.
 * no_answer, declined, cancelled, timeout and abandoned never do.
 */
export const endedByMessage = (call, userId) => {
  const by = call?.ended_by;
  if (by === null || by === undefined) return null;
  if (call.end_reason && call.end_reason !== "completed") return null;
  const id = typeof by === "object" ? by.id : by;
  if (sameId(id, userId)) return null;
  const name = by.display_name || displayNameFor(call, id);
  if (name) return `${name} has ended the call.`;
  return isCallHost(call, id) ? "The host has ended the call." : "A firm admin has ended the call.";
};

/** Banner text for the host_left frame; the name comes from call.participants. */
export const hostLeftMessage = (call, hostId) =>
  `${displayNameFor(call, hostId) || "The host"} left the call.`;

export const canEndCall = (call, { userId, isOwner }, thread) =>
  isLive(call) &&
  (isOwner ||
    sameId(call.started_by?.id, userId) ||
    (thread?.thread_type === "1:1" && Boolean(participantFor(call, userId))));

/**
 * How an unanswered call ended for `userId`: "declined" or "missed", or null if it was answered.
 * Uses participant states, which say who declined even when end_reason does not.
 * Accepts live calls (participants[].user.id) and message snapshots (participants[].user_id).
 */
export const unansweredOutcome = (call, userId) => {
  if (!call || call.answered_at) return null;
  const idOf = (person) => person.user_id ?? person.user?.id;
  const starter = call.started_by_id ?? call.started_by?.id;
  const people = call.participants || [];
  if (!sameId(starter, userId))
    return people.find((person) => sameId(idOf(person), userId))?.state === "declined"
      ? "declined"
      : "missed";
  const others = people.filter((person) => !sameId(idOf(person), starter));
  return others.length > 0 && others.every((person) => person.state === "declined")
    ? "declined"
    : "missed";
};

export const describeCall = (call) => {
  const kind = call?.kind === "audio" ? "Voice call" : "Video call";
  const reason = call?.end_reason;
  if (["no_answer", "declined", "cancelled"].includes(reason)) {
    return `${{ no_answer: "Missed", declined: "Declined", cancelled: "Cancelled" }[reason]} ${kind.toLowerCase()}`;
  }
  const seconds = Math.max(0, Number(call?.duration_seconds) || 0);
  const minutes = Math.floor(seconds / 60);
  const duration =
    minutes >= 60
      ? `${Math.floor(minutes / 60)} h ${minutes % 60} min`
      : minutes
        ? `${minutes} min`
        : `${seconds} sec`;
  return `${kind} · ${duration}${reason === "timeout" ? " (timed out)" : ""}`;
};

const ERROR_MESSAGES = {
  not_configured: "Video calling is not available in this environment yet.",
  not_allowed: "You do not have permission to perform this call action.",
  nobody_to_call: "There is nobody else in this conversation to call.",
  not_found: "This call or conversation is no longer available.",
  thread_locked: "Calling is unavailable because this conversation is unlinked.",
  not_live: "This call has ended.",
  not_ringing: "This invitation has already been answered or has expired.",
  not_in_room: "You have left the call. Use Rejoin to return.",
  too_many_participants: "This call has reached its 25-person limit.",
  invalid_target: "You can't ring yourself.",
  already_ringing: "That person is already being rung.",
  already_in_call: "That person is already in the call.",
  invalid_kind: "This call type is not supported.",
};

export const callError = (error, now = Date.now()) => {
  const response = error?.response;
  const data = response?.data || {};
  const retryAfter = response?.headers?.["retry-after"];
  const noRetry = retryAfter === null || retryAfter === undefined;
  const retrySeconds = noRetry ? 0 : Number(retryAfter);
  const retryAt = noRetry
    ? null
    : Number.isFinite(retrySeconds)
      ? now + Math.max(0, retrySeconds) * 1000
      : Date.parse(retryAfter);
  return {
    code: data.code || (response?.status === 429 ? "throttled" : "request_failed"),
    message:
      response?.status === 429
        ? "Too many attempts. Please wait before trying again."
        : ERROR_MESSAGES[data.code] ||
          data.detail ||
          error?.message ||
          "The call action could not be completed.",
    retryAt: Number.isFinite(retryAt) ? retryAt : null,
    uncertain: !response || response.status >= 500,
  };
};
